import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { coreAPI } from '../lib/api';
import { useFormatters } from '../contexts/PreferencesContext';
import { useBranding } from '../contexts/BrandingContext';
import { ArrowLeft, Loader2 } from 'lucide-react';
import DOMPurify from 'dompurify';

export default function Terms() {
  const { branding } = useBranding();
  const [apiContent, setApiContent] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    coreAPI.getLegalContent()
      .then((res) => {
        if (res.data.terms_content) {
          setApiContent(res.data.terms_content);
          setUpdatedAt(res.data.terms_updated_at);
        }
      })
      .catch(() => {
        // Silently fall back to static content
      })
      .finally(() => setLoading(false));
  }, []);

  // CR-6307: delegate to the preference-aware formatter, keeping the fallback.
  const { formatDate: fmtDate } = useFormatters();
  const formatDate = (dateStr: string | null) => (dateStr ? fmtDate(dateStr) : 'December 2024');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link
            to="/login"
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Login
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Terms & Conditions</h1>
          <p className="text-gray-500 mb-8">Last updated: {formatDate(updatedAt)}</p>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : apiContent ? (
            <div
              className="prose prose-gray max-w-none"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(apiContent) }}
            />
          ) : (
            <StaticTermsContent companyName={branding.companyName} />
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="text-center py-8">
        <p className="text-sm text-gray-500">
          {branding.companyName.replace(/Portal$/i, '').trim()} Portal | Secure Business Portal
        </p>
      </div>
    </div>
  );
}

function StaticTermsContent({ companyName }: { companyName: string }) {
  return (
    <div className="prose prose-gray max-w-none">
      <h2 className="text-xl font-semibold mt-6 mb-3">1. Acceptance of Terms</h2>
      <p className="text-gray-700 mb-4">
        By accessing and using the {companyName} Portal, you accept and agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use this service.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-3">2. Use of Service</h2>
      <p className="text-gray-700 mb-4">
        The {companyName} Portal is provided for business purposes to facilitate supplier management, purchasing, quality control, and related business operations. You agree to use this service only for lawful purposes and in accordance with these terms.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-3">3. User Accounts</h2>
      <p className="text-gray-700 mb-4">
        You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-3">4. Data and Content</h2>
      <p className="text-gray-700 mb-4">
        You retain ownership of any data you submit through the platform. By submitting data, you grant us the right to process and store this data as necessary to provide our services.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-3">5. Confidentiality</h2>
      <p className="text-gray-700 mb-4">
        All business information shared through the platform shall be treated as confidential. Users agree not to disclose or share any proprietary information accessed through the platform without proper authorization.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-3">6. Limitation of Liability</h2>
      <p className="text-gray-700 mb-4">
        To the maximum extent permitted by law, {companyName} shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use the service.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-3">7. Modifications</h2>
      <p className="text-gray-700 mb-4">
        We reserve the right to modify these terms at any time. Continued use of the service after any modifications constitutes acceptance of the updated terms.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-3">8. Contact</h2>
      <p className="text-gray-700 mb-4">
        For questions about these Terms & Conditions, please contact your system administrator or the {companyName} support team.
      </p>
    </div>
  );
}
